import React, { useState, useEffect, useMemo, useCallback } from "react";
import supabase from "../config/supabaseConfig";
import { professoresService } from "../services/professores.service";
import "../styles/RelatoriosNovo.css";

// Relatórios (patch 2026) — versão enxuta, DS-native. Fase 1: os 2 KPIs novos
// do handoff (Por Plano; Por Professor), agregados no backend via RPC
// (alunos_por_plano / aulas_realizadas_por_plano / aulas_realizadas_por_professor)
// — sem paginação client-side (que estourava o teto de 1000 do PostgREST).
// As contagens de AULAS respeitam o período selecionado (params p_inicio/p_fim).

// Ordem canônica de exibição dos planos; desconhecidos vão pro fim.
const ORDEM_PLANOS = [
  "8 Check-in",
  "12 Check-in",
  "16 Check-in",
  "Premium",
  "Black",
  "Sem plano",
];
const ordemPlano = (p) => {
  const i = ORDEM_PLANOS.indexOf(p);
  return i === -1 ? ORDEM_PLANOS.length : i;
};

const fmt = (n) => (n ?? 0).toLocaleString("pt-BR");

const isoHoje = () => new Date().toISOString().slice(0, 10);
const isoDiasAtras = (dias) => {
  const d = new Date();
  d.setDate(d.getDate() - dias);
  return d.toISOString().slice(0, 10);
};
const isoInicioAno = () => `${new Date().getFullYear()}-01-01`;
// dd/mm/aaaa a partir de "aaaa-mm-dd" (sem Date, evita desvio de fuso).
const fmtData = (iso) => {
  if (!iso) return "";
  const [a, m, d] = iso.split("-");
  return `${d}/${m}/${a}`;
};

// Barra de proporção (largura % sobre o maior valor da coluna).
const Barra = ({ valor, max }) => (
  <div className="rel-bar-track">
    <div
      className="rel-bar-fill"
      style={{ width: max > 0 ? `${(valor / max) * 100}%` : "0%" }}
    />
  </div>
);

// Barra empilhada de pontualidade (pontual / leve / significativo).
const BarraPont = ({ pontual, leve, sig, total }) => {
  const p = (n) => (total > 0 ? `${(n / total) * 100}%` : "0%");
  return (
    <div className="rel-stack">
      <span className="rel-stack-pontual" style={{ width: p(pontual) }} />
      <span className="rel-stack-leve" style={{ width: p(leve) }} />
      <span className="rel-stack-sig" style={{ width: p(sig) }} />
    </div>
  );
};

const pct1 = (n) => `${(n ?? 0).toFixed(1)}%`;

const RelatoriosNovo = () => {
  const [aba, setAba] = useState("plano");

  // Período (aulas). inicio/fim null = todo o período.
  const [inicio, setInicio] = useState("");
  const [fim, setFim] = useState("");

  const [carregandoBase, setCarregandoBase] = useState(true);
  const [carregandoAulas, setCarregandoAulas] = useState(true);
  const [erro, setErro] = useState(null);

  const [alunosPorPlano, setAlunosPorPlano] = useState([]);
  const [professores, setProfessores] = useState([]);
  const [aulasPorPlano, setAulasPorPlano] = useState([]);
  const [volumeProfessor, setVolumeProfessor] = useState([]);
  const [pontualidadeProf, setPontualidadeProf] = useState([]);

  // Base independente de período: alunos por plano (snapshot atual) + professores.
  useEffect(() => {
    let ativo = true;
    (async () => {
      try {
        setCarregandoBase(true);
        const [apl, profs] = await Promise.all([
          supabase.rpc("alunos_por_plano"),
          professoresService.getAll(),
        ]);
        if (apl.error) throw apl.error;
        if (!ativo) return;
        setAlunosPorPlano(apl.data || []);
        setProfessores(profs || []);
      } catch (err) {
        console.error("Erro ao carregar base de relatórios:", err);
        if (ativo) setErro("Erro ao carregar relatórios: " + err.message);
      } finally {
        if (ativo) setCarregandoBase(false);
      }
    })();
    return () => {
      ativo = false;
    };
  }, []);

  // Aulas por plano/professor — recarrega quando o período muda.
  useEffect(() => {
    let ativo = true;
    (async () => {
      try {
        setCarregandoAulas(true);
        const params = { p_inicio: inicio || null, p_fim: fim || null };
        const [axpl, vpr, ppr] = await Promise.all([
          supabase.rpc("aulas_realizadas_por_plano", params),
          supabase.rpc("volume_por_professor", params),
          supabase.rpc("pontualidade_por_professor", params),
        ]);
        if (axpl.error) throw axpl.error;
        if (vpr.error) throw vpr.error;
        if (ppr.error) throw ppr.error;
        if (!ativo) return;
        setAulasPorPlano(axpl.data || []);
        setVolumeProfessor(vpr.data || []);
        setPontualidadeProf(ppr.data || []);
      } catch (err) {
        console.error("Erro ao carregar aulas por período:", err);
        if (ativo) setErro("Erro ao carregar aulas: " + err.message);
      } finally {
        if (ativo) setCarregandoAulas(false);
      }
    })();
    return () => {
      ativo = false;
    };
  }, [inicio, fim]);

  const aplicarPreset = useCallback((preset) => {
    if (preset === "tudo") {
      setInicio("");
      setFim("");
    } else if (preset === "ano") {
      setInicio(isoInicioAno());
      setFim(isoHoje());
    } else if (preset === "90d") {
      setInicio(isoDiasAtras(90));
      setFim(isoHoje());
    }
  }, []);

  const presetAtivo = !inicio && !fim ? "tudo" : "custom";
  const periodoLabel =
    !inicio && !fim
      ? "Todo o período"
      : `${inicio ? fmtData(inicio) : "início"} – ${
          fim ? fmtData(fim) : "hoje"
        }`;

  // Por plano: junta alunos + aulas por plano numa lista ordenada.
  const linhasPlano = useMemo(() => {
    const mapa = new Map();
    alunosPorPlano.forEach((r) =>
      mapa.set(r.plano, {
        plano: r.plano,
        ativos: r.ativos || 0,
        inativos: r.inativos || 0,
        alunos: r.total || 0,
        aulas: 0,
      })
    );
    aulasPorPlano.forEach((r) => {
      const linha = mapa.get(r.plano) || {
        plano: r.plano,
        ativos: 0,
        inativos: 0,
        alunos: 0,
        aulas: 0,
      };
      linha.aulas = r.total || 0;
      mapa.set(r.plano, linha);
    });
    return [...mapa.values()].sort(
      (a, b) => ordemPlano(a.plano) - ordemPlano(b.plano)
    );
  }, [alunosPorPlano, aulasPorPlano]);

  // Por professor: aulas (turmas) + alunos atendidos (presenças) no período,
  // ordenado por aulas desc; inclui profs com 0 aula.
  const linhasProfessor = useMemo(() => {
    const vol = new Map(volumeProfessor.map((r) => [r.professor_id, r]));
    return professores
      .map((p) => {
        const r = vol.get(p.id);
        return {
          id: p.id,
          nome: p.nome,
          aulas: r?.total_aulas || 0,
          alunos: r?.total_alunos || 0,
        };
      })
      .sort((a, b) => b.aulas - a.aulas);
  }, [volumeProfessor, professores]);

  // Pontualidade: por professor (nome + contagens), ordenado por total desc.
  const linhasPontualidade = useMemo(() => {
    const mapa = new Map(professores.map((p) => [p.id, p.nome]));
    return pontualidadeProf
      .map((r) => ({
        id: r.professor_id,
        nome: mapa.get(r.professor_id) || "—",
        pontual: r.pontual || 0,
        leve: r.atraso_leve || 0,
        sig: r.atraso_significativo || 0,
        sem: r.sem_horario || 0,
        total: r.total || 0,
      }))
      .sort((a, b) => b.total - a.total);
  }, [pontualidadeProf, professores]);

  const pontGlobal = useMemo(() => {
    const g = linhasPontualidade.reduce(
      (acc, l) => ({
        pontual: acc.pontual + l.pontual,
        leve: acc.leve + l.leve,
        sig: acc.sig + l.sig,
        total: acc.total + l.total,
      }),
      { pontual: 0, leve: 0, sig: 0, total: 0 }
    );
    const pct = (n) => (g.total > 0 ? (n / g.total) * 100 : 0);
    return { ...g, pctPontual: pct(g.pontual), pctLeve: pct(g.leve), pctSig: pct(g.sig) };
  }, [linhasPontualidade]);

  const totalAlunos = linhasPlano.reduce((s, l) => s + l.alunos, 0);
  const totalAtivos = linhasPlano.reduce((s, l) => s + l.ativos, 0);
  // Por plano conta presenças (aula_alunos); por professor conta aulas (turmas).
  // São unidades diferentes → cada aba usa seu próprio total.
  const totalPresencas = linhasPlano.reduce((s, l) => s + l.aulas, 0);
  const totalAulasProf = linhasProfessor.reduce((s, l) => s + l.aulas, 0);
  const totalAlunosAtend = linhasProfessor.reduce((s, l) => s + l.alunos, 0);
  const maxAlunos = Math.max(1, ...linhasPlano.map((l) => l.alunos));
  const maxAulasPlano = Math.max(1, ...linhasPlano.map((l) => l.aulas));
  const maxAulasProf = Math.max(1, ...linhasProfessor.map((l) => l.aulas));

  const carregando = carregandoBase || carregandoAulas;

  return (
    <div className="relatorios-nova">
      <div className="rel-header">
        <h1>Relatórios</h1>
        <span className="rel-sub">Visão geral do sistema</span>
      </div>

      <div className="rel-tabs">
        <button
          className={`rel-tab ${aba === "plano" ? "active" : ""}`}
          onClick={() => setAba("plano")}
        >
          Por Plano
        </button>
        <button
          className={`rel-tab ${aba === "professor" ? "active" : ""}`}
          onClick={() => setAba("professor")}
        >
          Por Professor
        </button>
        <button
          className={`rel-tab ${aba === "pontualidade" ? "active" : ""}`}
          onClick={() => setAba("pontualidade")}
        >
          Pontualidade
        </button>
      </div>

      {/* Filtro de período (aplica às contagens de AULAS) */}
      <div className="rel-periodo">
        <div className="rel-periodo-presets">
          <button
            className={`rel-chip ${presetAtivo === "tudo" ? "active" : ""}`}
            onClick={() => aplicarPreset("tudo")}
          >
            Todo o período
          </button>
          <button className="rel-chip" onClick={() => aplicarPreset("ano")}>
            Este ano
          </button>
          <button className="rel-chip" onClick={() => aplicarPreset("90d")}>
            Últimos 90 dias
          </button>
        </div>
        <div className="rel-periodo-range">
          <label>
            De
            <input
              type="date"
              value={inicio}
              max={fim || undefined}
              onChange={(e) => setInicio(e.target.value)}
            />
          </label>
          <label>
            Até
            <input
              type="date"
              value={fim}
              min={inicio || undefined}
              onChange={(e) => setFim(e.target.value)}
            />
          </label>
        </div>
        <span className="rel-periodo-label">
          Aulas no período: <strong>{periodoLabel}</strong>
          {carregandoAulas && <span className="rel-periodo-load"> · …</span>}
        </span>
      </div>

      {erro && <div className="rel-erro">{erro}</div>}
      {carregando ? (
        <p className="rel-note">Carregando dados…</p>
      ) : aba === "plano" ? (
        <>
          <div className="rel-kpis">
            <div className="rel-kpi">
              <span className="rel-kpi-num">{fmt(totalAlunos)}</span>
              <span className="rel-kpi-label">Alunos (base atual)</span>
            </div>
            <div className="rel-kpi">
              <span className="rel-kpi-num">{fmt(totalAtivos)}</span>
              <span className="rel-kpi-label">Ativos</span>
            </div>
            <div className="rel-kpi">
              <span className="rel-kpi-num">{fmt(totalPresencas)}</span>
              <span className="rel-kpi-label">Aulas realizadas · período</span>
            </div>
          </div>

          <div className="rel-card">
            <div className="rel-card-title">Alunos e aulas por plano</div>
            <div className="rel-table-scroll">
              <table className="rel-table">
                <thead>
                  <tr>
                    <th>PLANO</th>
                    <th>ALUNOS</th>
                    <th>ATIVOS</th>
                    <th>INATIVOS</th>
                    <th className="rel-col-bar">DISTRIBUIÇÃO</th>
                    <th>AULAS · PERÍODO</th>
                    <th className="rel-col-bar">VOLUME</th>
                  </tr>
                </thead>
                <tbody>
                  {linhasPlano.map((l) => (
                    <tr key={l.plano}>
                      <td className="rel-plano">{l.plano}</td>
                      <td>
                        <strong>{fmt(l.alunos)}</strong>
                      </td>
                      <td>{fmt(l.ativos)}</td>
                      <td className="rel-muted">{fmt(l.inativos)}</td>
                      <td className="rel-col-bar">
                        <Barra valor={l.alunos} max={maxAlunos} />
                      </td>
                      <td>
                        <span className="rel-badge">{fmt(l.aulas)}</span>
                      </td>
                      <td className="rel-col-bar">
                        <Barra valor={l.aulas} max={maxAulasPlano} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : aba === "professor" ? (
        <>
          <div className="rel-kpis">
            <div className="rel-kpi">
              <span className="rel-kpi-num">{fmt(linhasProfessor.length)}</span>
              <span className="rel-kpi-label">Professores</span>
            </div>
            <div className="rel-kpi">
              <span className="rel-kpi-num">{fmt(totalAulasProf)}</span>
              <span className="rel-kpi-label">Aulas dadas · período</span>
            </div>
            <div className="rel-kpi">
              <span className="rel-kpi-num">{fmt(totalAlunosAtend)}</span>
              <span className="rel-kpi-label">Alunos atendidos · período</span>
            </div>
          </div>

          <div className="rel-card">
            <div className="rel-card-title">Volume de aulas por professor</div>
            <div className="rel-table-scroll">
              <table className="rel-table">
                <thead>
                  <tr>
                    <th>PROFESSOR</th>
                    <th>AULAS · PERÍODO</th>
                    <th>ALUNOS ATENDIDOS</th>
                    <th className="rel-col-bar">VOLUME</th>
                  </tr>
                </thead>
                <tbody>
                  {linhasProfessor.map((l) => (
                    <tr key={l.id}>
                      <td className="rel-plano">{l.nome}</td>
                      <td>
                        <span className="rel-badge">{fmt(l.aulas)}</span>
                      </td>
                      <td className="rel-muted">{fmt(l.alunos)}</td>
                      <td className="rel-col-bar">
                        <Barra valor={l.aulas} max={maxAulasProf} />
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="rel-total">
                    <td className="rel-plano">TOTAL GERAL</td>
                    <td>
                      <strong>{fmt(totalAulasProf)}</strong>
                    </td>
                    <td>
                      <strong>{fmt(totalAlunosAtend)}</strong>
                    </td>
                    <td className="rel-col-bar" />
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="rel-kpis">
            <div className="rel-kpi">
              <span className="rel-kpi-num rel-c-pontual">
                {pct1(pontGlobal.pctPontual)}
              </span>
              <span className="rel-kpi-label">Pontualidade geral</span>
            </div>
            <div className="rel-kpi">
              <span className="rel-kpi-num rel-c-leve">
                {pct1(pontGlobal.pctLeve)}
              </span>
              <span className="rel-kpi-label">Atraso leve</span>
            </div>
            <div className="rel-kpi">
              <span className="rel-kpi-num rel-c-sig">
                {pct1(pontGlobal.pctSig)}
              </span>
              <span className="rel-kpi-label">Atraso significativo</span>
            </div>
            <div className="rel-kpi">
              <span className="rel-kpi-num">{fmt(pontGlobal.total)}</span>
              <span className="rel-kpi-label">Aulas · período</span>
            </div>
          </div>

          <div className="rel-legenda">
            <span>
              <i className="rel-dot rel-c-pontual-bg" /> Pontual (:49–:05)
            </span>
            <span>
              <i className="rel-dot rel-c-leve-bg" /> Atraso leve (:06–:15)
            </span>
            <span>
              <i className="rel-dot rel-c-sig-bg" /> Atraso significativo (:16–:48)
            </span>
          </div>

          <div className="rel-card">
            <div className="rel-card-title">Pontualidade por professor</div>
            <div className="rel-table-scroll">
              <table className="rel-table">
                <thead>
                  <tr>
                    <th>PROFESSOR</th>
                    <th>PONTUAL</th>
                    <th>ATRASO LEVE</th>
                    <th>ATRASO SIG.</th>
                    <th className="rel-col-bar">DISTRIBUIÇÃO</th>
                    <th>% PONTUAL</th>
                  </tr>
                </thead>
                <tbody>
                  {linhasPontualidade.map((l) => (
                    <tr key={l.id}>
                      <td className="rel-plano">{l.nome}</td>
                      <td className="rel-c-pontual">{fmt(l.pontual)}</td>
                      <td className="rel-c-leve">{fmt(l.leve)}</td>
                      <td className="rel-c-sig">{fmt(l.sig)}</td>
                      <td className="rel-col-bar">
                        <BarraPont
                          pontual={l.pontual}
                          leve={l.leve}
                          sig={l.sig}
                          total={l.total}
                        />
                      </td>
                      <td>
                        <span className="rel-badge">
                          {pct1(l.total > 0 ? (l.pontual / l.total) * 100 : 0)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="rel-total">
                    <td className="rel-plano">TOTAL GERAL</td>
                    <td>
                      <strong>{fmt(pontGlobal.pontual)}</strong>
                    </td>
                    <td>
                      <strong>{fmt(pontGlobal.leve)}</strong>
                    </td>
                    <td>
                      <strong>{fmt(pontGlobal.sig)}</strong>
                    </td>
                    <td className="rel-col-bar" />
                    <td>
                      <strong>{pct1(pontGlobal.pctPontual)}</strong>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default RelatoriosNovo;
